import * as THREE from 'three';
import { ATHLETE, COURT, REDE } from '../config';
import type { Input } from '../core/Input';
import { clamp } from '../core/math';
import type { Court } from '../world/Court';
import { LIMITE_DA_PRAIA } from '../world/praia';
import { construirAtleta, type AtletaVisual } from './buildAthlete';
import { Corpos } from './montarCorpo';
import { VISUAL_PADRAO, chaveDoVisual, type Visual } from './corpos';
import { Animador, estadoDoMotor } from './Animador';
import type { EstadoDoCorpo } from './animacoes';
import { Motor } from './Motor';
import { direcaoDoTeclado } from './controle';

const _direcao = new THREE.Vector3();
/** Parado, pro criador: de pe', sem andar, sem gesto. */
const PARADO: Readonly<EstadoDoCorpo> = {
  noChao: true, mergulhando: false, levantando: false, pousando: false, segurandoBola: false,
  velocidade: 0, anguloDoAndar: 0, gesto: null, marcaDoGesto: 0,
};
// Sem gesto, sempre: quem passeia pela praia nao toca em bola.
const _estado: EstadoDoCorpo = {
  noChao: true, mergulhando: false, levantando: false, pousando: false, segurandoBola: false,
  velocidade: 0, anguloDoAndar: 0, gesto: null, marcaDoGesto: 0,
};

/**
 * Voce, fora da quadra.
 *
 * Nao e' um `Athlete`, e isso e' a decisao inteira. Athlete nasce amarrado a um
 * `Court` e a um `Side` — e' o que da' sentido a "minha meia quadra", "a rede
 * esta' nessa direcao", "o Match me chama pra sacar". Quem anda pela praia nao
 * tem lado nem quadra, e fazer o Athlete fingir que nao tem espalharia um `if`
 * por tudo: no limite de area, no saque, na mira, na escolha de acao.
 *
 * Entao sao dois corpos. Voce entra na quadra: o banhista some e um `Human`
 * ocupa o lado. Voce sai: o `Human` vira bot de novo e o banhista reaparece na
 * beira. O corte e' limpo porque `Arena.ocupar` e `Arena.liberar` ja' trocam
 * quem joga sem derrubar a partida.
 *
 * O que ele tem em comum com um atleta e' o que deve ter: o mesmo `Motor` (o
 * motor nunca soube o que e' quadra — recebe uma funcao de limite) e o mesmo
 * corpo low-poly.
 */
export class Banhista {
  readonly motor: Motor;
  readonly visual: AtletaVisual;

  camera: THREE.PerspectiveCamera | null = null;
  input: Input | null = null;

  private corpo: THREE.Object3D | null = null;
  private corpos: Corpos | null = null;
  private animador: Animador | null = null;
  private readonly cor: number;
  /** Voce: o corpo que o criador montou. */
  private aparencia: Visual = VISUAL_PADRAO;
  private montado = '';
  /** Como no `Athlete`: sem corpo nenhum enquanto as pecas chegam. */
  private esperandoCorpo = true;

  /**
   * As quadras que ele nao pode atravessar.
   *
   * O Motor nunca soube o que e' quadra — recebe uma funcao de limite e
   * pergunta a cada quadro. Aqui essa funcao e' "a praia, menos as redes".
   */
  /**
   * Ate' onde da' pra andar. Trocavel porque o CENARIO decide, nao o banhista.
   *
   * Na praia e' a caixa das tres quadras; dentro do estadio e' o anel de LED,
   * que fica bem mais perto. Sem isto, sair da quadra no estadio deixava sair
   * do ESTADIO tambem — o limite da praia e' 44 m em x, e o bowl encolhido tem
   * 24. Andava-se atravessando a arquibancada pra areia vazia atras dela.
   */
  limite: { x: number; z: number } = LIMITE_DA_PRAIA;

  constructor(cor: number, quadras: readonly Court[] = []) {
    this.cor = cor;
    this.visual = construirAtleta(cor, COURT.serveBallHeight);
    this.visual.capsulas.visible = false;

    this.motor = new Motor((posicao, out) => {
      out.set(
        clamp(posicao.x, -this.limite.x, this.limite.x),
        posicao.y,
        clamp(posicao.z, -this.limite.z, this.limite.z),
      );
      // Na rede, o banhista tambem entra ate' o fundo e e' empurrado de volta
      // (`Arena.encostarNaRede`); o poste continua duro.
      for (const quadra of quadras) quadra.desviarDaRede(out, ATHLETE.radius, out, REDE.corpo.folgaMinima);
      return out;
    });
  }

  /**
   * A mesma troca de pele do atleta.
   *
   * Duplicada e nao herdada porque `Banhista` NAO e' um `Athlete` — e' a
   * decisao que abre este arquivo. O que os dois compartilham de verdade
   * (`Motor`, `Animador`, a leitura do estado, a montagem em `Corpos`) ja'
   * esta' compartilhado; o que sobra aqui e' guardar e trocar a pele.
   */
  usarModelo(corpos: Corpos | null): void {
    this.corpos = corpos;
    this.esperandoCorpo = false;
    this.montado = '';
    this.remontar();
  }

  vestir(visual: Visual): void {
    this.aparencia = visual;
    this.visual.pintar(visual.camisa ?? this.cor);
    this.remontar();
  }

  private remontar(): void {
    const chave = this.corpos ? chaveDoVisual(this.aparencia) : '';
    if (chave === this.montado && (this.corpo !== null) === (this.corpos !== null)) return;
    this.montado = chave;

    if (this.corpo) {
      this.visual.root.remove(this.corpo);
      Corpos.descartar(this.corpo);
      this.corpo = null;
    }
    this.animador?.dispose();
    this.animador = null;

    if (this.corpos) {
      this.corpo = this.corpos.montar(this.aparencia);
      this.visual.root.add(this.corpo);
      this.animador = new Animador(this.corpo, this.corpos.animacoes(this.aparencia.familia));
    }

    this.visual.capsulas.visible = this.corpos === null && !this.esperandoCorpo;
  }

  get objeto(): THREE.Object3D { return this.visual.root; }
  get posicao(): THREE.Vector3 { return this.motor.posicao; }

  /** Reaparece em algum lugar, olhando pra alguma direcao. */
  colocarEm(posicao: THREE.Vector3, olharPara: THREE.Vector3): void {
    this.motor.colocarEm(posicao, olharPara);
    this.visual.root.position.copy(this.motor.posicao);
  }

  /**
   * Posando no criador: num ponto, virado pra `giro`, na espera do pack.
   *
   * Mexe so' no desenho, e nao no `Motor`: fechado o criador, o proximo
   * `update` devolve o corpo pra onde o motor esta'.
   */
  posar(posicao: THREE.Vector3, giro: number, dt: number): void {
    this.visual.root.position.copy(posicao);
    this.visual.root.rotation.set(0, giro, 0);
    this.animador?.update(PARADO, dt);
  }

  update(dt: number): void {
    if (this.input) {
      direcaoDoTeclado(this.input, this.camera, _direcao);
      this.motor.moverPara(_direcao);

      // Aqui o corpo VIRA pra onde anda: nao ha' bola pra encarar, e um boneco
      // que anda de lado pela praia inteira parece quebrado.
      this.motor.encarar(_direcao);

      if (this.input.wasPressed('Space')) this.motor.pular();
    }

    this.motor.update(dt);
    this.visual.root.position.copy(this.motor.posicao);
    this.motor.aplicarRotacao(this.visual.root, dt);
    this.animador?.update(estadoDoMotor(this.motor, _estado), dt);
  }

  dispose(): void {
    this.animador?.dispose();
    if (this.corpo) Corpos.descartar(this.corpo);
    this.visual.dispose();
  }
}
